/**
 * Configure Wizard — success screen helpers (UI only).
 * Consumes ConfigureSubmissionResponse fields already returned by the API.
 * Source: docs/pep/V1.0-CONFIGURE-WIZARD-INTEGRATION-AUDIT.md (Cas C)
 */
import type {
  ActivationResultSummary,
  ConfigureSubmissionRecap,
  ConfigureSubmissionResponse,
} from "@itaas/pep-contracts";

export type ConfigureSuccessKind = "environment_ready" | "request_received";

export interface ConfigureSuccessView {
  kind: ConfigureSuccessKind;
  reference: string;
  notificationStatus: ConfigureSubmissionResponse["notificationStatus"];
  lifecycleStatus: ConfigureSubmissionResponse["lifecycleStatus"];
  recap: ConfigureSubmissionRecap;
  activation?: ActivationResultSummary;
}

/** Runtime provisioned a usable / partial environment. */
export function isEnvironmentReady(activation?: ActivationResultSummary | null): boolean {
  if (!activation) return false;
  return (
    activation.businessOutcome === "ACTIVATED" || activation.businessOutcome === "PARTIAL"
  );
}

/**
 * Accept Configure response for the success screen.
 * Prefer Runtime outcomes; keep commercial notification as fallback when no activation tenant path.
 */
export function isConfigureSubmissionAccepted(result: ConfigureSubmissionResponse): boolean {
  if (result.success !== true || !result.reference) return false;
  if (isEnvironmentReady(result.activation)) return true;
  return result.notificationStatus === "sent";
}

export function toConfigureSuccessView(result: ConfigureSubmissionResponse): ConfigureSuccessView | null {
  if (!isConfigureSubmissionAccepted(result)) return null;
  return {
    kind: isEnvironmentReady(result.activation) ? "environment_ready" : "request_received",
    reference: result.reference,
    notificationStatus: result.notificationStatus,
    lifecycleStatus: result.lifecycleStatus,
    recap: result.recap,
    activation: result.activation,
  };
}
